import React, { useCallback, useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const SOURCES = [
  'Goibibo', 'MakeMyTrip', 'Booking.com', 'Expedia',
  'Agoda', 'Cleartrip', 'Yatra', 'EaseMyTrip',
  'Direct', 'Corporate', 'Travel Agent', 'Other',
];
const MODES = ['BANK_TRANSFER', 'NEFT', 'RTGS', 'CHEQUE', 'UPI', 'ONLINE', 'CASH', 'OTHER'];
const MODE_LABEL = {
  BANK_TRANSFER: 'Bank Transfer', NEFT: 'NEFT', RTGS: 'RTGS',
  CHEQUE: 'Cheque', UPI: 'UPI', ONLINE: 'Online', CASH: 'Cash', OTHER: 'Other',
};
const STATUS_COLOR = { RECEIVED: '#2563eb', RECONCILED: '#16a34a' };

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtMoney = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_FORM = {
  payment_source: '',
  amount: '',
  payment_date: todayIso(),
  payment_mode: 'BANK_TRANSFER',
  reference_no: '',
  remarks: '',
};

export default function PmsAgentPayments({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const chainId    = selectedProperty?.chain_id || '';
  const propertyId = selectedProperty?.id || '';

  const [payments, setPayments]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [message, setMessage]       = useState('');

  // Filters
  const [fromDate, setFromDate]     = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
  const [toDate, setToDate]         = useState(todayIso);
  const [filterSource, setFilterSource] = useState('');

  // Summary totals
  const [totals, setTotals]         = useState({ received: 0, reconciled: 0 });

  // Modal
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null); // null = new, else payment object
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);

  // Link reservation modal
  const [linkModal, setLinkModal]   = useState(null); // payment object
  const [linkForm, setLinkForm]     = useState({ reservation_no: '', guest_name: '', amount: '' });
  const [linkSaving, setLinkSaving] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    'Content-Type': 'application/json',
  }), [token, chainId, propertyId]);

  const load = useCallback(async () => {
    if (!chainId || !propertyId) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (filterSource) params.set('source', filterSource);
      const res = await fetch(`/api/v1/pms/agent-payments?${params}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load'); return; }
      setPayments(data.payments || []);
      // Compute totals
      const t = { received: 0, reconciled: 0 };
      for (const p of (data.payments || [])) {
        if (p.status === 'RECONCILED') t.reconciled += Number(p.amount || 0);
        else t.received += Number(p.amount || 0);
      }
      setTotals(t);
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }, [chainId, propertyId, fromDate, toDate, filterSource, headers]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      payment_source: p.payment_source,
      amount: String(p.amount),
      payment_date: String(p.payment_date || '').slice(0, 10),
      payment_mode: p.payment_mode || 'BANK_TRANSFER',
      reference_no: p.reference_no || '',
      remarks: p.remarks || '',
    });
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!form.payment_source) return setError('Select a payment source');
    if (!form.amount || Number(form.amount) <= 0) return setError('Enter a valid amount');
    setSaving(true); setError('');
    try {
      const url = editing
        ? `/api/v1/pms/agent-payments/${editing.payment_id}`
        : '/api/v1/pms/agent-payments';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed'); return; }
      setMessage(editing ? 'Payment updated' : 'Payment recorded successfully');
      setShowForm(false);
      load();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Delete payment of ${fmtMoney(p.amount)} from ${p.payment_source}?`)) return;
    try {
      const res = await fetch(`/api/v1/pms/agent-payments/${p.payment_id}`, { method: 'DELETE', headers: headers() });
      if (!res.ok) { const d = await res.json(); return setError(d.error || 'Delete failed'); }
      setMessage('Payment deleted');
      load();
    } catch { setError('Network error'); }
  };

  const markReconciled = async (p) => {
    try {
      const res = await fetch(`/api/v1/pms/agent-payments/${p.payment_id}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ status: p.status === 'RECONCILED' ? 'RECEIVED' : 'RECONCILED' }),
      });
      if (!res.ok) { const d = await res.json(); return setError(d.error || 'Update failed'); }
      load();
    } catch { setError('Network error'); }
  };

  const saveLink = async () => {
    if (!linkForm.reservation_no) return setError('Enter reservation number');
    setLinkSaving(true);
    try {
      const res = await fetch(`/api/v1/pms/agent-payments/${linkModal.payment_id}/links`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          reservation_id: 0,
          reservation_no: linkForm.reservation_no,
          guest_name: linkForm.guest_name,
          amount: linkForm.amount ? Number(linkForm.amount) : null,
        }),
      });
      if (!res.ok) { const d = await res.json(); return setError(d.error || 'Link failed'); }
      setLinkModal(null);
      setLinkForm({ reservation_no: '', guest_name: '', amount: '' });
      setMessage('Reservation linked');
      load();
    } catch { setError('Network error'); }
    finally { setLinkSaving(false); }
  };

  const removeLink = async (paymentId, linkId) => {
    if (!window.confirm('Remove this reservation link?')) return;
    try {
      await fetch(`/api/v1/pms/agent-payments/${paymentId}/links/${linkId}`, { method: 'DELETE', headers: headers() });
      load();
    } catch { setError('Network error'); }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' };
  const inp = { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem', width: '100%', boxSizing: 'border-box', outline: 'none' };
  const btn = (bg, color = '#fff') => ({ padding: '0.5rem 1.1rem', border: 'none', borderRadius: 8, background: bg, color, cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem' });
  const badge = (color) => ({ display: 'inline-block', padding: '0.18rem 0.6rem', borderRadius: 20, background: color + '18', color, fontWeight: 700, fontSize: '0.73rem' });

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1.25rem 1.5rem', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#1a2151' }}>Agent / OTA Payments</h2>
          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>Record payments received from Goibibo, MakeMyTrip, Booking.com, and other sources</p>
        </div>
        <button onClick={openNew} style={btn('#2563eb')}>+ Record Payment</button>
      </div>

      {/* ── Status message ── */}
      {message && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '0.75rem', color: '#15803d', fontWeight: 600, fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}>{message}<span style={{ cursor: 'pointer' }} onClick={() => setMessage('')}>✕</span></div>}
      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '0.75rem', color: '#dc2626', fontWeight: 600, fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}>{error}<span style={{ cursor: 'pointer' }} onClick={() => setError('')}>✕</span></div>}

      {/* ── Summary cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
        {[
          { label: 'Total Received', value: totals.received + totals.reconciled, color: '#2563eb' },
          { label: 'Pending Reconciliation', value: totals.received, color: '#d97706' },
          { label: 'Reconciled', value: totals.reconciled, color: '#16a34a' },
        ].map(s => (
          <div key={s.label} style={{ ...card, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.color }}>{fmtMoney(s.value)}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ ...card, marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>From Date</label>
          <input type="date" value={fromDate} onChange={e => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }} style={{ ...inp, width: 150 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>To Date</label>
          <input type="date" value={toDate} min={fromDate || undefined} onChange={e => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }} style={{ ...inp, width: 150 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>Source</label>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ ...inp, width: 160 }}>
            <option value="">All Sources</option>
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={load} style={btn('#2563eb')}>Apply</button>
      </div>

      {/* ── Payments table ── */}
      <div style={card}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Loading...</div>
        ) : payments.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2.5rem 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>💳</div>
            No payments recorded for this period.
            <div style={{ marginTop: 8 }}><button onClick={openNew} style={btn('#2563eb')}>Record First Payment</button></div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Date', 'Source', 'Amount', 'Mode', 'Reference', 'Status', 'Linked Reservations', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => {
                const links = Array.isArray(p.linked_reservations) ? p.linked_reservations : [];
                return (
                  <tr key={p.payment_id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>{fmtDate(p.payment_date)}</td>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{p.payment_source}</td>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#1a2151' }}>{fmtMoney(p.amount)}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>{MODE_LABEL[p.payment_mode] || p.payment_mode}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.reference_no || ''}>{p.reference_no || '—'}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <span style={badge(STATUS_COLOR[p.status] || '#6b7280')}>{p.status}</span>
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      {links.length === 0 ? (
                        <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>None</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {links.map(l => (
                            <div key={l.link_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
                              <span style={{ fontWeight: 600 }}>{l.reservation_no}</span>
                              {l.guest_name && <span style={{ color: '#6b7280' }}>{l.guest_name}</span>}
                              {l.amount && <span style={{ color: '#2563eb' }}>{fmtMoney(l.amount)}</span>}
                              <button onClick={() => removeLink(p.payment_id, l.link_id)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.75rem', padding: 0 }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button onClick={() => openEdit(p)} style={{ ...btn('#f3f4f6', '#374151'), padding: '0.3rem 0.65rem', fontSize: '0.75rem' }}>Edit</button>
                        <button onClick={() => markReconciled(p)} style={{ ...btn(p.status === 'RECONCILED' ? '#fef9c3' : '#f0fdf4', p.status === 'RECONCILED' ? '#92400e' : '#15803d'), padding: '0.3rem 0.65rem', fontSize: '0.75rem' }}>
                          {p.status === 'RECONCILED' ? 'Unreconcile' : 'Reconcile'}
                        </button>
                        <button onClick={() => { setLinkModal(p); setLinkForm({ reservation_no: '', guest_name: '', amount: '' }); }} style={{ ...btn('#eff6ff', '#2563eb'), padding: '0.3rem 0.65rem', fontSize: '0.75rem' }}>Link Res.</button>
                        <button onClick={() => deletePayment(p)} style={{ ...btn('#fef2f2', '#dc2626'), padding: '0.3rem 0.65rem', fontSize: '0.75rem' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Record / Edit Payment Modal ── */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(520px,96vw)', background: '#fff', borderRadius: 16, boxShadow: '0 24px 48px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
            <div style={{ background: '#2563eb', color: '#fff', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem' }}>{editing ? 'Edit Payment' : 'Record Payment'}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Record a lump-sum payment received from an OTA or agency</div>
              </div>
              <button onClick={() => setShowForm(false)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Payment Source *</label>
                  <select value={form.payment_source} onChange={e => setForm(f => ({ ...f, payment_source: e.target.value }))} style={inp}>
                    <option value="">Select source...</option>
                    {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Amount (₹) *</label>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="50000" style={inp} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Payment Date *</label>
                  <input type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Payment Mode</label>
                  <select value={form.payment_mode} onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))} style={inp}>
                    {MODES.map(m => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Reference / Transaction No.</label>
                <input type="text" value={form.reference_no} onChange={e => setForm(f => ({ ...f, reference_no: e.target.value }))} placeholder="Bank ref, UTR, cheque no..." style={inp} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Remarks</label>
                <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={2} placeholder="Optional notes..." style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: 4 }}>
                <button onClick={() => setShowForm(false)} style={btn('#6b7280')}>Cancel</button>
                <button onClick={saveForm} disabled={saving} style={btn(saving ? '#93c5fd' : '#2563eb')}>
                  {saving ? 'Saving...' : (editing ? 'Update Payment' : 'Record Payment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Link Reservation Modal ── */}
      {linkModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(440px,96vw)', background: '#fff', borderRadius: 16, boxShadow: '0 24px 48px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
            <div style={{ background: '#1a2151', color: '#fff', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800 }}>Link Reservation</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>{linkModal.payment_source} · {fmtMoney(linkModal.amount)}</div>
              </div>
              <button onClick={() => setLinkModal(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Reservation No. *</label>
                <input type="text" value={linkForm.reservation_no} onChange={e => setLinkForm(f => ({ ...f, reservation_no: e.target.value }))} placeholder="e.g. RES-1234" style={inp} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Guest Name</label>
                <input type="text" value={linkForm.guest_name} onChange={e => setLinkForm(f => ({ ...f, guest_name: e.target.value }))} placeholder="Optional" style={inp} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Amount Allocated (₹)</label>
                <input type="number" min="0" step="0.01" value={linkForm.amount} onChange={e => setLinkForm(f => ({ ...f, amount: e.target.value }))} placeholder="Optional" style={inp} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setLinkModal(null)} style={btn('#6b7280')}>Cancel</button>
                <button onClick={saveLink} disabled={linkSaving} style={btn('#1a2151')}>
                  {linkSaving ? 'Linking...' : 'Link Reservation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
